/**
 * RxJS ajax pretty much interchangable with axios.
 * Using RxJS ajax as it's already an observable to use with redux-observable.
 */
import { ajax } from 'rxjs/ajax';

import { getCurrentTimestamp } from '../utils/utils';

const getParams = ( x ) =>
	`?${ Object.keys( x )
		.map( ( p ) => `&${ p }=${ x[ p ] }` )
		.join( '' ) }`;

export function getEntries( page, config, newestEntry ) {
	// For page 1, use 'latest' to always get fresh entries.
	// This avoids stale data when the HTML page is cached (CDN/Varnish)
	// but entries have been added since the page was cached.
	// For other pages, use the newestEntry as an anchor for consistent pagination.
	const lastKnownEntry =
		page === 1
			? 'latest'
			: `${ newestEntry.id || config.latest_entry_id }-${
					newestEntry.timestamp || config.latest_entry_timestamp
				}`;

	const settings = {
		url: `${ config.endpoint_url }get-entries/${ page }/${ lastKnownEntry }`,
		method: 'GET',
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function polling( newestEntryTimestamp, config ) {
	// Round both timestamps to bucket boundaries for higher cache hitrate.
	// This groups clients with similar state into the same cache cohort.
	// Clients may receive duplicate entries, but applyUpdate() handles
	// duplicates via entry IDs - receiving the same entry twice is safe.
	const refreshInterval = parseInt( config.refresh_interval, 10 );
	const startTimestamp =
		Math.floor( ( newestEntryTimestamp + 1 ) / refreshInterval ) *
		refreshInterval;
	const endTimestamp =
		Math.floor( getCurrentTimestamp() / refreshInterval ) * refreshInterval;

	const settings = {
		url: `${ config.endpoint_url }entries/${
			startTimestamp || 0
		}/${ endTimestamp }/`,
		method: 'GET',
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function createEntry( entry, config, nonce = false ) {
	const settings = {
		url: `${ config.endpoint_url }crud/`,
		method: 'POST',
		body: {
			crud_action: 'insert',
			post_id: config.post_id,
			content: entry.content,
			author_id: entry.author,
			contributor_ids: entry.contributors,
		},
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce || config.nonce,
			'cache-control': 'no-cache',
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function updateEntry( entry, config, nonce = false ) {
	const settings = {
		url: `${ config.endpoint_url }crud/`,
		method: 'POST',
		body: {
			crud_action: 'update',
			post_id: config.post_id,
			entry_id: entry.id,
			content: entry.content,
			author_id: entry.author,
			contributor_ids: entry.contributors,
		},
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce || config.nonce,
			'cache-control': 'no-cache',
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function deleteEntry( id, config, nonce = false ) {
	const settings = {
		url: `${ config.endpoint_url }crud/`,
		method: 'POST',
		body: {
			crud_action: 'delete',
			post_id: config.post_id,
			entry_id: id,
		},
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce || config.nonce,
			'cache-control': 'no-cache',
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function getEvents( config, newestEntry ) {
	const settings = {
		url: `${ config.endpoint_url }get-key-events/${
			newestEntry.id || config.latest_entry_id
		}-${ newestEntry.timestamp || config.latest_entry_timestamp }`,
		crossDomain: config.cross_domain,
		method: 'GET',
	};

	return ajax( settings );
}

export function jumpToEvent( id, config, newestEntry ) {
	const settings = {
		url: `${ config.endpoint_url }jump-to-key-event/${ id }/${
			newestEntry.id || 0
		}-${ newestEntry.timestamp || 0 }`,
		crossDomain: config.cross_domain,
		method: 'GET',
	};

	return ajax( settings );
}

export function deleteEvent( entry, config, nonce = false ) {
	const settings = {
		url: `${ config.endpoint_url }crud/`,
		method: 'POST',
		body: {
			crud_action: 'delete_key',
			post_id: config.post_id,
			entry_id: entry.id,
			content: entry.content,
		},
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce || config.nonce,
			'cache-control': 'no-cache',
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function getAuthors( term, config ) {
	const settings = {
		url: `${ config.autocomplete[ 3 ].url }${ term }`,
		method: 'GET',
		headers: {
			'X-WP-Nonce': config.nonce,
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function getHashtags( term, config ) {
	const settings = {
		url: `${ config.autocomplete[ 2 ].url }${ term }`,
		method: 'GET',
		headers: {
			'X-WP-Nonce': config.nonce,
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function getPreview( content, config ) {
	const settings = {
		url: `${ config.endpoint_url }preview`,
		method: 'POST',
		body: {
			entry_content: content,
		},
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config.nonce,
		},
		crossDomain: config.cross_domain,
	};

	return ajax( settings );
}

export function uploadImage( formData ) {
	const location = window.location;

	const settings = {
		url: `${ location.protocol }//${ location.host }/wp-admin/admin-ajax.php`,
		method: 'POST',
		body: formData,
	};

	return ajax( settings );
}

export function getMedia( params ) {
	const location = window.location;

	const settings = {
		url: `${ location.protocol }//${
			location.host
		}/wp-json/wp/v2/media${ getParams( params ) }`,
		method: 'GET',
		headers: {
			'X-WP-Nonce': window.liveblog_settings?.nonce || '',
		},
	};

	return ajax( settings );
}
